import React from 'react';
import { Primitive } from '@ryvora/react-primitive';
import { createContextScope, type Scope } from '@ryvora/react-context';
import { useComposedRefs } from '@ryvora/react-compose-refs';
import { composeEventHandlers } from '@ryvora/primitive';

/** ----------------------------------------- ScrollableList ----------------------------------------- */

const SCROLLABLE_LIST_NAME = 'ScrollableList';

type ScopedProps<P> = P & { __scopeScrollableList?: Scope };

const [createScrollableListContext, createScrollableListScope] =
  createContextScope(SCROLLABLE_LIST_NAME);

interface ScrollContextType {
  firstRef: React.RefObject<React.ComponentRef<typeof Primitive.div> | null>;
  lastRef: React.RefObject<React.ComponentRef<typeof Primitive.div> | null>;
  scrollRef: React.RefObject<React.ComponentRef<typeof Primitive.div> | null>;
  items: React.ReactNode[];
  disabledLeft: boolean;
  setDisabledLeft: React.Dispatch<React.SetStateAction<boolean>>;
  disabledRight: boolean;
  setDisabledRight: React.Dispatch<React.SetStateAction<boolean>>;
  isScrollReady?: boolean;
}

const [ScrollableListProvider, useScrollableListContextInternal] =
  createScrollableListContext<ScrollContextType>(SCROLLABLE_LIST_NAME);

export function useScrollContext(callerName: string, scope?: Scope): ScrollContextType {
  const context = useScrollableListContextInternal(callerName, scope);
  if (!context) {
    throw new Error(`\`${callerName}\` must be used within \`${SCROLLABLE_LIST_NAME}\``);
  }
  return context;
}

type ScrollableListElement = React.ComponentRef<typeof Primitive.div>;
type PrimitiveDivProps = React.ComponentPropsWithoutRef<typeof Primitive.div>;
interface ScrollableListProps extends PrimitiveDivProps {}

const ScrollableList = React.forwardRef<ScrollableListElement, ScopedProps<ScrollableListProps>>(
  ({ __scopeScrollableList, children, ...props }, forwardedRef) => {
    const firstRef = React.useRef<React.ComponentRef<typeof Primitive.div>>(null);
    const lastRef = React.useRef<React.ComponentRef<typeof Primitive.div>>(null);
    const scrollRef = React.useRef<React.ComponentRef<typeof Primitive.div>>(null);

    const [isScrollReady, setIsScrollReady] = React.useState(false);
    const [disabledLeft, setDisabledLeft] = React.useState(true);
    const [disabledRight, setDisabledRight] = React.useState(false);

    React.useEffect(() => {
      if (scrollRef.current) {
        setIsScrollReady(true);
      }
    }, []);

    let identifiedViewport: React.ReactElement | null = null;
    const otherChildren: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (
        React.isValidElement(child) &&
        typeof child.type !== 'string' &&
        (child.type as { displayName?: string }).displayName === VIEWPORT_NAME
      ) {
        identifiedViewport = child;
      } else {
        otherChildren.push(child);
      }
    });

    let viewportWithRefs: React.ReactElement | null = identifiedViewport;

    if (identifiedViewport) {
      const viewportProps = (identifiedViewport as { props: { children?: React.ReactNode } }).props;
      const itemsFromViewport = React.Children.toArray(viewportProps.children);

      const itemsWithRefs = itemsFromViewport.map((item, idx) => {
        if (!React.isValidElement(item)) return item;
        if (idx === 0)
          return React.cloneElement(item as React.ReactElement<React.RefAttributes<HTMLElement>>, {
            ref: firstRef,
          });
        if (idx === itemsFromViewport.length - 1)
          return React.cloneElement(item as React.ReactElement<React.RefAttributes<HTMLElement>>, {
            ref: lastRef,
          });
        return item;
      });

      viewportWithRefs = React.cloneElement(identifiedViewport, {}, itemsWithRefs);
    }

    return (
      <ScrollableListProvider
        scope={__scopeScrollableList}
        firstRef={firstRef}
        lastRef={lastRef}
        scrollRef={scrollRef}
        items={React.Children.toArray(children)}
        disabledLeft={disabledLeft}
        setDisabledLeft={setDisabledLeft}
        disabledRight={disabledRight}
        setDisabledRight={setDisabledRight}
        isScrollReady={isScrollReady}
      >
        <Primitive.div data-state-scrollable-list-root {...props} ref={forwardedRef}>
          {viewportWithRefs}
          {otherChildren}
        </Primitive.div>
      </ScrollableListProvider>
    );
  }
);

ScrollableList.displayName = SCROLLABLE_LIST_NAME;

/** ----------------------------------------- ScrollableListViewport ----------------------------------------- */

const VIEWPORT_NAME = 'ScrollableListViewport';
type ScrollableListViewportElement = React.ComponentRef<typeof Primitive.div>;
interface ScrollableListViewportProps extends PrimitiveDivProps {}

const ScrollableListViewport = React.forwardRef<
  ScrollableListViewportElement,
  ScopedProps<ScrollableListViewportProps>
>(({ __scopeScrollableList, children, style, onScroll, ...props }, forwardedRef) => {
  const context = useScrollContext(VIEWPORT_NAME, __scopeScrollableList);
  const { setDisabledLeft, setDisabledRight } = context;
  const composedRefs = useComposedRefs(forwardedRef, context.scrollRef);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const scrollLeft = target.scrollLeft;
    const scrollWidth = target.scrollWidth;
    const clientWidth = target.clientWidth;

    console.log({
      scrollLeft,
      scrollWidth,
      clientWidth,
      isAtStart: scrollLeft === 0,
      isAtEnd: clientWidth + scrollLeft >= scrollWidth - 1,
    });

    setDisabledLeft(scrollLeft === 0);
    setDisabledRight(clientWidth + scrollLeft >= scrollWidth - 1);
  };

  return (
    <Primitive.div
      data-state-scrollable-list-viewport
      ref={composedRefs}
      onScroll={composeEventHandlers(onScroll, handleScroll)}
      {...props}
    >
      {children}
    </Primitive.div>
  );
});

ScrollableListViewport.displayName = VIEWPORT_NAME;

/** ----------------------------------------- ScrollableListItem ----------------------------------------- */

const ITEM_NAME = 'ScrollableListItem';
type ScrollableListItemElement = React.ComponentRef<typeof Primitive.div>;
interface ScrollableListItemProps extends PrimitiveDivProps {}

const ScrollableListItem = React.forwardRef<
  ScrollableListItemElement,
  ScopedProps<ScrollableListItemProps>
>(({ __scopeScrollableList, ...props }, forwardedRef) => {
  return <Primitive.div data-state-scrollable-list-item {...props} ref={forwardedRef} />;
});

ScrollableListItem.displayName = ITEM_NAME;

/** ----------------------------------------- ScrollableListPreviousButton ----------------------------------------- */

const PREVIOUS_BUTTON_NAME = 'ScrollableListPreviousButton';
type ScrollableListPreviousButtonElement = React.ComponentRef<typeof Primitive.button>;
type PrimitiveButtonProps = React.ComponentPropsWithoutRef<typeof Primitive.button>;
interface ScrollableListPreviousButtonProps extends PrimitiveButtonProps {}

const ScrollableListPreviousButton = React.forwardRef<
  ScrollableListPreviousButtonElement,
  ScopedProps<ScrollableListPreviousButtonProps>
>(({ __scopeScrollableList, onClick, ...props }, forwardedRef) => {
  const { disabledLeft, scrollRef, isScrollReady } = useScrollContext(
    PREVIOUS_BUTTON_NAME,
    __scopeScrollableList
  );

  const handleClick = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };

  if (!isScrollReady) return null;

  return (
    <Primitive.button
      data-state-scrollable-list-prev-button
      data-disabled={disabledLeft ? '' : undefined}
      disabled={disabledLeft}
      onClick={composeEventHandlers(onClick, handleClick)}
      {...props}
      ref={forwardedRef}
    />
  );
});

ScrollableListPreviousButton.displayName = PREVIOUS_BUTTON_NAME;

/** ----------------------------------------- ScrollableListNextButton ----------------------------------------- */

const NEXT_BUTTON_NAME = 'ScrollableListNextButton';
type ScrollableListNextButtonElement = React.ComponentRef<typeof Primitive.button>;
interface ScrollableListNextButtonProps extends PrimitiveButtonProps {}

const ScrollableListNextButton = React.forwardRef<
  ScrollableListNextButtonElement,
  ScopedProps<ScrollableListNextButtonProps>
>(({ __scopeScrollableList, onClick, ...props }, forwardedRef) => {
  const { disabledRight, scrollRef, isScrollReady } = useScrollContext(
    NEXT_BUTTON_NAME,
    __scopeScrollableList
  );

  const handleClick = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  if (!isScrollReady) return null;

  return (
    <Primitive.button
      data-state-scrollable-list-next-button
      aria-disabled={disabledRight || undefined}
      data-disabled={disabledRight ? '' : undefined}
      disabled={disabledRight}
      onClick={composeEventHandlers(onClick, handleClick)}
      {...props}
      ref={forwardedRef}
    />
  );
});

ScrollableListNextButton.displayName = NEXT_BUTTON_NAME;

export { createScrollableListScope };

const Root = ScrollableList;
const Viewport = ScrollableListViewport;
const Item = ScrollableListItem;
const PreviousButton = ScrollableListPreviousButton;
const NextButton = ScrollableListNextButton;

export {
  ScrollableList,
  ScrollableListViewport,
  ScrollableListItem,
  ScrollableListPreviousButton,
  ScrollableListNextButton,
  //
  Root,
  Viewport,
  Item,
  PreviousButton,
  NextButton,
};

export type {
  ScrollableListProps,
  ScrollableListViewportProps,
  ScrollableListItemProps,
  ScrollableListPreviousButtonProps,
  ScrollableListNextButtonProps,
};
